/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Box,
  Container,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Checkbox,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { convertSex } from "@helpers/funciones";
import { CURP, RFC_PFAE_SIN_CLAVE, RFC_HOMOCLAVE } from "@utils/expreciones";
import { CurpQuery, CurpResponse } from "@interfaces/index";
import { setModalErrorVisible } from "@store/slices/appSlice";

import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";

import {
  useLazyGetCurpSecondGuaranteeQuery,
  usePatchPersonSecondGuaranteeMutation,
  usePatchUsuarioStatusSecondGuaranteeMutation,
} from "@api/second_guarantee_api/secondGuaranteeApi";
import { setCurretStepSecondGuarantee, setUserSecondGuaranteeRfcCurp } from "@store/slices/segundoAvalSlice";
import { useNextStepAndPatch } from "@hooks/useStatusConfigSecondGuarantee";

const RegistroSegundoAvalRfcCurp = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const shareholder = useSelector((state: RootState) => state.secondGuarante.guarantee);
  const { nextStatus } = useNextStepAndPatch(shareholder.status_config);

  const [getCurp] = useLazyGetCurpSecondGuaranteeQuery();
  const [patchPerson] = usePatchPersonSecondGuaranteeMutation();
  const [patchNextStatus] = usePatchUsuarioStatusSecondGuaranteeMutation();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: {
      rfc: shareholder.personalInfo?.rfc,
      homoclave: shareholder.personalInfo?.rfc_homoclave,
      curp: shareholder.personalInfo?.curp,
      confirmCheck: false,
    },
    validationSchema: yup.object().shape({
      rfc: yup.string().required("Campo requerido").matches(RFC_PFAE_SIN_CLAVE, "Formato de RFC no válido"),
      homoclave: yup.string().required("Campo requerido").matches(RFC_HOMOCLAVE, "Formato de homoclave no válido"),
      curp: yup.string().required("Campo requerido").matches(CURP, "Formato de CURP no válido"),
      confirmCheck: yup.boolean().isTrue().required(""),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        await patchPerson({
          personId: String(shareholder.id),
          body: {
            rfc: values.rfc + values.homoclave,
            curp: values.curp,
          },
        }).unwrap();

        dispatch(
          setUserSecondGuaranteeRfcCurp({
            rfc: values.rfc,
            curp: values.curp,
            rfc_homoclave: values.homoclave,
          }),
        );

        await patchNextStatus({
          usuario: String(shareholder.user_id),
          body: {
            status_id: String(nextStatus?.status_id),
          },
        }).unwrap();

        dispatch(
          setCurretStepSecondGuarantee({
            code: String(nextStatus?.code),
            url: String(nextStatus?.url),
            sequence: Number(nextStatus?.sequence),
            status_id: String(nextStatus?.status_id),
          }),
        );

        navigate(String(nextStatus?.url));
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "RegistroSegundoAvalRfcCurp",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const extractRFC = async (rfc: string) => {
    if (!rfc || typeof rfc !== "string" || rfc.length < 10) {
      return "";
    }

    return rfc.substring(0, 10);
  };

  useEffect(() => {
    const fetchCurp = async () => {
      try {
        setIsLoading(true);
        const birthStateNumber = parseInt(shareholder.personalInfo?.birth_state, 10);

        const birthStateFormatted =
          birthStateNumber >= 1 && birthStateNumber <= 9
            ? `0${birthStateNumber}`
            : shareholder.personalInfo?.birth_state;

        const payloadGetCurp: CurpQuery = {
          nombres: shareholder.personalInfo?.name,
          apellido_paterno: shareholder.personalInfo?.first_lastname,
          apellido_materno: shareholder.personalInfo?.second_lastname,
          estado_nacimiento: birthStateFormatted,
          fecha_nacimiento: dayjs(shareholder.personalInfo?.date_birth, "YYYY-MM-DD").format("DD-MM-YYYY"),
          sexo: convertSex(shareholder.personalInfo?.sex),
        };
        const responseGetCurp: CurpResponse = await getCurp(payloadGetCurp).unwrap();
        const rfc = await extractRFC(responseGetCurp.data.curp);
        formik.setFieldValue("curp", responseGetCurp.data.curp);
        formik.setFieldValue("rfc", rfc);
      } catch (error) {
        console.log("Hubo un error al obtener la CURP: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurp();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        <Box
          sx={{
            mt: isMobileOrTablet ? 2 : 4,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            Registro Aval
          </Typography>

          <Typography
            variant={"body1"}
            fontWeight={300}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
            mt={4}
          >
            Confirma el RFC y CURP del accionista mayoritario
          </Typography>
        </Box>

        <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
          <form onSubmit={formik.handleSubmit} noValidate>
            <Grid container columnSpacing={2}>
              <Grid item xs={8}>
                <TextField
                  id="rfc"
                  name="rfc"
                  label="RFC accionista"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={(e) => {
                    const input = e.target;
                    const value = e.target.value.toUpperCase();
                    const start = input.selectionStart;
                    formik.handleChange(e);
                    formik.setFieldValue("rfc", value);
                    setTimeout(() => {
                      input.setSelectionRange(start, start);
                    }, 0);
                  }}
                  inputProps={{ maxLength: 10 }}
                  onBlur={formik.handleBlur}
                  value={formik.values.rfc}
                  error={formik.touched.rfc && Boolean(formik.errors.rfc)}
                  helperText={formik.touched.rfc && formik.errors.rfc}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="homoclave"
                  name="homoclave"
                  label="Homoclave"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={(e) => {
                    const input = e.target;
                    const value = e.target.value.toUpperCase();
                    const start = input.selectionStart;
                    formik.handleChange(e);
                    formik.setFieldValue("homoclave", value);
                    setTimeout(() => {
                      input.setSelectionRange(start, start);
                    }, 0);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.homoclave}
                  error={formik.touched.homoclave && Boolean(formik.errors.homoclave)}
                  helperText={formik.touched.homoclave && formik.errors.homoclave}
                  inputProps={{ maxLength: 3 }}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="curp"
                  name="curp"
                  label="CURP"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={(e) => {
                    const input = e.target;
                    const value = e.target.value.toUpperCase();
                    const start = input.selectionStart;
                    formik.handleChange(e);
                    formik.setFieldValue("curp", value);
                    setTimeout(() => {
                      input.setSelectionRange(start, start);
                    }, 0);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.curp}
                  error={formik.touched.curp && Boolean(formik.errors.curp)}
                  helperText={formik.touched.curp && formik.errors.curp}
                  inputProps={{ maxLength: 18 }}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>
              <Grid item xs={12} mt={1}>
                <FormControlLabel
                  control={<Checkbox id="confirmCheck" name="confirmCheck" onChange={formik.handleChange} />}
                  label="Confirmo que el RFC y CURP son correctos"
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
              <Button
                variant="default"
                type="submit"
                color="primary"
                disabled={!formik.isValid || isLoading || formik.isSubmitting}
              >
                Continuar
              </Button>
            </Box>
          </form>
        </Container>
      </Grid>

      {isMobileOrTablet == false && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default RegistroSegundoAvalRfcCurp;
